import { ReactElement } from 'react';
import { Nav, NavDropdown } from 'react-bootstrap';
import { Link, NavLink, useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { SelectCallback } from 'react-bootstrap/helpers';
import { useAtomValue } from 'jotai';
import LineAwesome from '../LineAwesome/LineAwesome';
import DropDownTitle from '../Dropdown/DropDownTitle';
import { dsgvoService } from '../../services/dsgvo-service';
import { regionStore } from '../../store/jotai/store';
import { userAtom } from '../../store/jotai/atoms/user.atom';
import { authAtom } from '../../store/jotai/atoms/auth.atom';
import { removeCookie } from '../../core/api/strapi-client';
import { basketAtom } from '../../store/jotai/atoms/basket.atom';
import { shippingRatesAtom } from '../../store/jotai/atoms/shipping-rates.atom';
import { WEBSHOP_ENABLED } from '../../constants/Settings';
import {
  totalUnreadAtom,
  conversationsAtom,
  activeConversationAtom,
  messagesAtom
} from '../../store/jotai/atoms/conversations.atom';
import { AccountMenuItem, getAccountMenuItems } from './accountMenuItems';
import './CustomNavigation.scss';

export type CustomNavigationProps = {
  toggleCart: () => void;
  searchToggle: () => void;
  toggleBookmarks: () => void;
  isMobile?: boolean;
  onLinkClick?: () => void;
  handleSelect: SelectCallback;
};

export const CustomNavigation = ({
  toggleCart,
  searchToggle,
  toggleBookmarks,
  isMobile = false,
  onLinkClick,
  handleSelect
}: CustomNavigationProps): ReactElement => {
  const { t } = useTranslation();
  const history = useHistory();

  const user = useAtomValue(userAtom, { store: regionStore });
  const basket = useAtomValue(basketAtom, { store: regionStore });
  const totalUnread = useAtomValue(totalUnreadAtom, { store: regionStore });

  const itemCount =
    basket?.basket_items?.flatMap((v) => v.items).reduce((sum, i) => sum + i.quantity, 0) ?? 0;
  const unreadLabel = totalUnread > 9 ? '9+' : totalUnread;

  const handleLinkClick = (): void => {
    if (onLinkClick) {
      onLinkClick();
    }
  };

  const handleLogout = (): void => {
    regionStore.set(userAtom, null);
    regionStore.set(authAtom, 'unauthenticated');
    regionStore.set(basketAtom, null);
    regionStore.set(shippingRatesAtom, null);
    regionStore.set(conversationsAtom, { asUser: [], asVendor: [] });
    regionStore.set(activeConversationAtom, null);
    regionStore.set(messagesAtom, []);
    regionStore.set(totalUnreadAtom, 0);

    removeCookie();
    handleLinkClick();

    history.push('/');
  };

  const handleAccountAction = (item: AccountMenuItem): void => {
    if (item.action === 'logout') {
      handleLogout();
    } else {
      dsgvoService.setCookieOverlayState(true);
    }
  };

  const accountTitle = (
    <>
      <LineAwesome icon='user' />
      &nbsp;
      <span>{`${user?.firstName ?? ''} ${user?.lastName ?? ''}`}</span>
      {totalUnread > 0 && <span className='custom-navigation__badge'>{unreadLabel}</span>}
    </>
  );

  const accountDropdown = (
    <NavDropdown className='login-nav-item' alignRight title={accountTitle} id='account'>
      {getAccountMenuItems(user).map((item) => {
        const label = (
          <>
            <LineAwesome icon={item.icon} /> {item.label}
            {item.showUnreadBadge && totalUnread > 0 && (
              <span className='custom-navigation__badge'>{unreadLabel}</span>
            )}
          </>
        );

        return item.to ? (
          <NavDropdown.Item key={item.id} onClick={handleLinkClick} as={Link} to={item.to}>
            {label}
          </NavDropdown.Item>
        ) : (
          <NavDropdown.Item key={item.id} onClick={() => handleAccountAction(item)}>
            {label}
          </NavDropdown.Item>
        );
      })}
    </NavDropdown>
  );

  const loginLink = (
    <Nav.Link
      as={NavLink}
      activeClassName='active'
      className='login-nav-item'
      to='/login'
      onClick={handleLinkClick}>
      <LineAwesome icon='sign-in-alt' /> {t('navbar.loginRegister')}
    </Nav.Link>
  );

  return (
    <Nav className='custom-navigation ml-auto' onSelect={handleSelect}>
      {isMobile && (user?.id ? accountDropdown : loginLink)}

      <Nav.Link onClick={handleLinkClick} as={NavLink} exact activeClassName='active' to='/'>
        {t('navbar.home')}
      </Nav.Link>
      <Nav.Link onClick={handleLinkClick} as={NavLink} activeClassName='active' to='/region'>
        {t('navbar.region')}
      </Nav.Link>
      {WEBSHOP_ENABLED && (
        <Nav.Link onClick={handleLinkClick} as={NavLink} activeClassName='active' to='/produkte'>
          {t('navbar.onlineShop')}
        </Nav.Link>
      )}
      <Nav.Link
        onClick={handleLinkClick}
        as={NavLink}
        activeClassName='active'
        to='/händlerübersicht'>
        {t('navbar.operators')}
      </Nav.Link>
      <Nav.Link
        onClick={handleLinkClick}
        as={NavLink}
        activeClassName='active'
        to='/händler-werden'>
        {t('navbar.information')}
      </Nav.Link>

      {isMobile ? (
        <>
          <div className='pt-3 w-100' />
          <Nav.Link
            className='mobile-footer-links'
            onClick={handleLinkClick}
            as={NavLink}
            activeClassName='active'
            to='/content/impressum'>
            {t('navbar.imprint')}
          </Nav.Link>
          <Nav.Link
            className='mobile-footer-links'
            onClick={handleLinkClick}
            as={NavLink}
            activeClassName='active'
            to='/content/datenschutzerklaerung'>
            {t('navbar.dsgvo')}
          </Nav.Link>
          <Nav.Link
            className='mobile-footer-links'
            onClick={handleLinkClick}
            as={NavLink}
            activeClassName='active'
            to='/content/agb-2'>
            {t('navbar.agb')}
          </Nav.Link>
        </>
      ) : (
        <>
          {user?.id ? accountDropdown : loginLink}

          {user?.id && (
            <Nav.Link className='icon-only' onClick={toggleBookmarks}>
              <DropDownTitle className='d-inline-block' icon='heart' iconClass='mr-1' title='' />
            </Nav.Link>
          )}

          {WEBSHOP_ENABLED && (
            <Nav.Link className='icon-only' onClick={toggleCart}>
              <DropDownTitle
                className='d-inline-block'
                icon='shopping-cart'
                iconClass='mr-1'
                title=''
                badgeClass='ml-1'
                badgeVariant='success'
                badgeValue={itemCount || 0}
              />
            </Nav.Link>
          )}

          <Nav.Link className='icon-only searchIcon mr-3' onClick={searchToggle}>
            <LineAwesome icon='search' className='custom-navigation__search-icon' />
          </Nav.Link>
        </>
      )}
    </Nav>
  );
};
